// AVR-01 (height decoding) and AVR-02 (processing channels) — README.md.

import type { BuildContext } from '../context';
import type { Issue } from '../types';
import { countNoun, productName, issueId } from '../copy';

export function checkAvr01(ctx: BuildContext): Issue[] {
  if (!ctx.avUnit) return [];
  const hasHeights = ctx.overheadIds.length > 0;
  if (!hasHeights) return [];
  const { atmos, dtsx } = ctx.avUnit.specs.heightDecoding.v;
  if (atmos || dtsx) return [];
  return [{
    id: issueId('AVR-01', [ctx.avUnit.entry.id]),
    rule: 'AVR-01',
    severity: 'error',
    text: `${productName(ctx.avUnit.product)} can't decode height channels.`,
    fix: 'Choose a receiver with Dolby Atmos.',
    subjects: [ctx.avUnit.entry.id],
  }];
}

/** `B.S.H` per README AVR-02 — B/S/H come from the ACTUALLY DERIVED channel
 *  counts (bed, whether any LFE exists, height), not from the speaker-setup
 *  wizard's layout enum. This is the conservative reading: everything else
 *  in this package treats channels as geometry-derived and never trusts a
 *  stored/selected layout string, and AVR-02's first clause (bed + height >
 *  processingChannels) already operates on the same derived counts — using
 *  a different source for the second clause would let the two halves of
 *  one rule disagree about what "the layout" is. */
export function derivedLayoutString(bed: number, hasSub: boolean, height: number): string {
  const s = hasSub ? 1 : 0;
  return height > 0 ? `${bed}.${s}.${height}` : `${bed}.${s}`;
}

/**
 * Does one of a receiver's listed layouts cover this build? A manual lists
 * its largest configurations: Denon's AVR-X1800H lists 7.1 and 5.1.2, not
 * 5.1 or stereo. A build with no more bed, subwoofer or height channels than
 * one listed layout runs on the receiver too, since unused channels are set
 * to "none" in its speaker setup. Plain membership called stereo and 5.1
 * unsupported on every receiver whose manual lists only its largest layouts.
 */
export function layoutCovered(listed: readonly string[], bed: number, sub: number, height: number): boolean {
  return listed.some((layout) => {
    const [b = 0, s = 0, h = 0] = layout.split('.').map(Number);
    return bed <= b && sub <= s && height <= h;
  });
}

export function checkAvr02(ctx: BuildContext): Issue[] {
  if (!ctx.avUnit) return [];
  const bed = ctx.bedIds.length;
  const height = ctx.overheadIds.length;
  const n = bed + height;
  const { processingChannels, supportedLayouts } = ctx.avUnit.specs;
  const processing = processingChannels.v;

  const overCapacity = n > processing;
  // Every layout in the catalogue's `LAYOUTS` enum (av-catalog/schema.ts)
  // starts from a 2-channel front bed (`2.0` is the smallest) — there is no
  // "1.0" (center-only) or "0.0.2" (overheads-only) entry, so a build that
  // hasn't got its front pair yet derives a layout string NO receiver's
  // `supportedLayouts` can ever contain, real or not. A center-only or
  // overheads-only room is a legitimate in-progress build (docs/plans/
  // 2026-09-15-gear-v2-brief.md), not an unsupported one — skip the
  // membership check until there's an actual front bed to check against.
  let layoutUnsupported = false;
  if (supportedLayouts && bed >= 2) {
    const sub = ctx.subIds.length > 0 ? 1 : 0;
    layoutUnsupported = !layoutCovered(supportedLayouts.v, bed, sub, height);
  }
  if (!overCapacity && !layoutUnsupported) return [];

  const name = productName(ctx.avUnit.product);
  const base = { id: issueId('AVR-02', [ctx.avUnit.entry.id]), rule: 'AVR-02' as const, severity: 'error' as const, subjects: [ctx.avUnit.entry.id] };
  if (!overCapacity) {
    // Enough channels, but the manual doesn't list this layout.
    const layout = derivedLayoutString(bed, ctx.subIds.length > 0, height);
    return [{ ...base, text: `${name} doesn't support ${layout}.`, fix: `Choose a receiver that supports ${layout}.` }];
  }
  const extra = n - processing;
  return [{
    ...base,
    text: `${name} supports ${processing} speakers; your layout has ${n}.`,
    fix: `Choose a receiver with ${n}-channel processing, or remove ${countNoun(extra, 'speaker')}.`,
  }];
}
